import type { Method } from 'axios';
import axios from 'axios';
import { expandDateRangeParams } from '@utils/dateRangeWire';

/**
 * Internal: applies the TZ wire treatment to a params object. Call sites
 * don't use this directly — they just pass `tz` to whatever fetcher they're
 * calling, and the fetcher applies this internally.
 */
const applyTz = (params: Record<string, any> | undefined, tz: string | undefined): Record<string, any> | undefined => {
    if (!tz) return params;
    const expanded = expandDateRangeParams(params ?? {}) as Record<string, any>;
    return { ...expanded, tz };
};

export { applyTz };

/**
 * APIs used by the Insights platform.
 */
export const enum Api {
    Auth = 'auth',
    Orgs = 'orgweb',
    Designs = 'designweb',
    Reports = 'reports',
    ReportsV2 = 'reporting',
    Cuts = 'cutweb',
    Systems = 'systemweb',
    CutState = 'cutstateweb',
    Tickets = 'tickets',
    Audit = 'audit',
}

/**
 * Get the API subdomain from environment variable.
 * Defaults to 'dev' if not set.
 */
const getApiSubdomain = (): string => {
    return import.meta.env.VITE_API_SUBDOMAIN || 'dev';
};

/**
 * Create the API URI for the configured environment.
 *
 * - In production builds: Uses relative paths (CloudFront proxies)
 * - In development: Uses full URLs based on VITE_API_SUBDOMAIN
 *
 * Set environment with: yarn env:switch
 */
export const createUri = (api: Api, path = '') => {
    if (!import.meta.env.DEV) {
        // Production build - use relative paths (CloudFront proxy)
        return path;
    }
    
    const subdomain = getApiSubdomain();
    return `https://${api}.${subdomain}.moutfittersync.net${path}`;
};

type ApiRequester<Input, Output> = (args: {
    body?: Input;
    token: string;
    params?: Record<string, any>;
    /** Pass the user's selected timezone to opt into TZ-aware request handling. */
    tz?: string;
}) => Promise<Output>;

type ApiFunction<Enum> = <Input, Output>(route: Enum, method: Method) => ApiRequester<Input, Output>;

/**
 * Composable API requests. Pass `tz` to opt into TZ handling; otherwise
 * params go through unchanged.
 */
export const createApi = <Enum>(uri: string): ApiFunction<Enum> => {
    const instance = axios.create({ baseURL: uri });

    return <Input, Output>(route: Enum, method: Method) => {
        return async ({
            body,
            token,
            params,
            tz,
        }: {
            body?: Input;
            token: string;
            params?: Record<string, any>;
            tz?: string;
        }): Promise<Output> => {
            const { data } = await instance(route as string, {
                method,
                headers: { Authorization: `Bearer ${token}` },
                data: body,
                params: applyTz(params, tz),
            });

            return data as Output;
        };
    };
};
